/* H&O Judge.me presentation. No review values, verification states, app
   settings, interactive star inputs or provider event handlers are changed. */
(() => {
  if (window.__hoJudgeMePresentation) return;
  window.__hoJudgeMePresentation = true;

  // The app teleports media-viewer review text outside the product widget.
  // Observe only its display-content panel, never the modal controls or form.
  const ROOT = '#judgeme_product_reviews, .jdgm-modal.jdgm-widget-revamp .jm-slide > .jm-content';
  const HEADING = 'What other stationery aficionados are saying';
  const widgets = new Map();
  const pending = new WeakSet();
  const originalHeadings = new WeakMap();

  function readRating(element) {
    // Read the existing accessible rating, never the review count or a default
    // score. Unknown/localized formats keep their original provider stars.
    const match = /^\s*(\d+(?:[.,]\d+)?)\s+(?:out of|of)\s+(\d+)\s+stars?\s*$/i.exec(element.getAttribute('aria-label') || '');
    if (!match) return null;
    const value = Number(match[1].replace(',', '.'));
    const maximum = Number(match[2]);
    if (!Number.isFinite(value) || !Number.isInteger(maximum) || maximum < 1 || maximum > 10 || value < 0 || value > maximum) return null;
    return { value, maximum };
  }

  function renderRating(element) {
    const rating = readRating(element);
    const existing = element.querySelector(':scope > .ho-review-stars');
    if (!rating) {
      existing?.remove();
      element.removeAttribute('data-ho-rating');
      return;
    }
    const key = `${rating.value}/${rating.maximum}`;
    if (element.dataset.hoRating === key && existing) return;
    existing?.remove();
    const group = document.createElement('span');
    group.className = 'ho-review-stars';
    group.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < rating.maximum; index++) {
      const star = document.createElement('span');
      star.className = 'ho-review-star';
      const fill = document.createElement('span');
      fill.className = 'ho-review-star__fill';
      fill.style.width = `${Math.round(Math.min(1, Math.max(0, rating.value - index)) * 10000) / 100}%`;
      star.append(fill);
      group.append(star);
    }
    // Retain the provider's font/SVG DOM for its own hydration and safe fallback.
    element.append(group);
    element.dataset.hoRating = key;
  }

  function apply(root) {
    const english = /^en(?:-|$)/i.test(document.documentElement.lang);
    root.querySelectorAll('.jm-review-widget-minimal-header__title').forEach(heading => {
      if (english) {
        if (heading.textContent !== HEADING) {
          originalHeadings.set(heading, heading.textContent);
          heading.textContent = HEADING;
        }
      } else if (heading.textContent === HEADING && originalHeadings.has(heading)) {
        heading.textContent = originalHeadings.get(heading);
      }
    });
    // Only noninteractive rating summaries. Leave review-writing inputs alone.
    root.querySelectorAll('.jm-star-rating[data-ho-rating]').forEach(element => {
      if (element.matches('.jm-review-item .jm-star-rating[role="img"]')) return;
      element.querySelector(':scope > .ho-review-stars')?.remove();
      element.removeAttribute('data-ho-rating');
    });
    root.querySelectorAll('.jm-review-item .jm-star-rating[role="img"]').forEach(renderRating);
    root.querySelectorAll('.jm-verified-buyer-badge').forEach(badge => {
      const label = badge.querySelector('.jm-verified-buyer-badge__text');
      const existing = badge.querySelector('[data-ho-verified-check]');
      if (!label?.textContent.trim()) { existing?.remove(); return; }
      if (existing) return;
      const check = document.createElement('span');
      check.dataset.hoVerifiedCheck = '';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      badge.prepend(check);
    });
  }

  function schedule(root) {
    if (pending.has(root)) return;
    pending.add(root);
    queueMicrotask(() => {
      pending.delete(root);
      if (root.isConnected) apply(root);
    });
  }

  function observe(root) {
    if (widgets.has(root)) return;
    apply(root);
    const observer = new MutationObserver(() => schedule(root));
    observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'role'] });
    widgets.set(root, observer);
  }

  function discover(node) {
    if (node.nodeType === Node.ELEMENT_NODE && node.matches(ROOT)) observe(node);
    node.querySelectorAll?.(ROOT).forEach(observe);
  }

  function start() {
    discover(document);
    // App widgets can mount after page load or be replaced by section rendering.
    // Detailed updates are observed only inside each mounted review widget.
    const discovery = new MutationObserver(mutations => {
      widgets.forEach((observer, root) => {
        if (!root.isConnected) {
          observer.disconnect();
          widgets.delete(root);
        }
      });
      mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const owner = node.closest(ROOT);
        if (!owner || !widgets.has(owner)) discover(node);
      }));
    });
    discovery.observe(document.body, { childList: true, subtree: true });
    const localeObserver = new MutationObserver(() => widgets.forEach((_, root) => schedule(root)));
    localeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
